import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  ShoppingBag, 
  Pill, 
  Users, 
  FolderOpen, 
  UserCircle,
  ExternalLink,
  Play,
  FileText,
  BookOpen,
  Dumbbell,
  Heart,
  MessageCircle,
  Lock,
  Trophy,
  Target,
  Flame,
  Star,
  TrendingUp,
  Calendar,
  Award,
  Loader2,
  MapPin,
  Clock,
  CheckCircle2,
  UserPlus,
  UserMinus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResourceCenterPage = () => {
  const [activeTab, setActiveTab] = useState('formacion');
  const [progressReport, setProgressReport] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [eventos, setEventos] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [apuntandose, setApuntandose] = useState(null);

  useEffect(() => {
    fetchProgressReport();
    fetchEventos();
  }, []);

  const fetchEventos = async () => {
    setLoadingEventos(true);
    try {
      const response = await axios.get(`${API}/eventos`);
      setEventos(response.data.eventos || []);
    } catch (error) {
      console.error('Failed to fetch eventos:', error);
    } finally {
      setLoadingEventos(false);
    }
  };

  const handleApuntarse = async (eventoId, estaApuntado) => {
    setApuntandose(eventoId);
    try {
      if (estaApuntado) {
        await axios.post(`${API}/eventos/${eventoId}/desapuntarse`);
        toast.success('Te has desapuntado del evento');
      } else {
        await axios.post(`${API}/eventos/${eventoId}/apuntarse`);
        toast.success('¡Te has apuntado al evento!');
      }
      fetchEventos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setApuntandose(null);
    }
  };

  const getTipoEventoColor = (tipo) => {
    switch (tipo) {
      case 'quedada': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'reto': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'taller': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'competicion': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    }
  };

  const getTipoEventoLabel = (tipo) => {
    switch (tipo) {
      case 'quedada': return 'Quedada';
      case 'reto': return 'Reto';
      case 'taller': return 'Taller';
      case 'competicion': return 'Competición';
      default: return 'Evento';
    }
  };

  const formatFecha = (fecha) => {
    const date = new Date(fecha + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const fetchProgressReport = async () => {
    try {
      const response = await axios.get(`${API}/me/progress-report`);
      setProgressReport(response.data);
    } catch (error) {
      console.error('Failed to fetch progress report:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  return (
    <ClientLayout>
      <div className="space-y-6 py-6 animate-fade-in" data-testid="resource-center">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
            <BookOpen className="h-7 w-7 text-primary" />
            Centro de Recursos del Guardian
          </h1>
          <p className="text-muted-foreground mt-1">
            Todo lo que necesitas para tu camino fitness
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-3 md:grid-cols-6 gap-1 bg-card border border-border p-1 h-auto">
            <TabsTrigger 
              value="formacion" 
              className="flex flex-col items-center gap-1 py-2 px-1 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <GraduationCap className="h-4 w-4" />
              Formación
            </TabsTrigger>
            <TabsTrigger 
              value="equipamiento"
              className="flex flex-col items-center gap-1 py-2 px-1 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <ShoppingBag className="h-4 w-4" />
              Equipamiento
            </TabsTrigger>
            <TabsTrigger 
              value="suplementacion"
              className="flex flex-col items-center gap-1 py-2 px-1 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Pill className="h-4 w-4" />
              Suplementación
            </TabsTrigger>
            <TabsTrigger 
              value="comunidad"
              className="flex flex-col items-center gap-1 py-2 px-1 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Users className="h-4 w-4" />
              Comunidad
            </TabsTrigger>
            <TabsTrigger 
              value="recursos"
              className="flex flex-col items-center gap-1 py-2 px-1 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <FolderOpen className="h-4 w-4" />
              Recursos
            </TabsTrigger>
            <TabsTrigger 
              value="personal"
              className="flex flex-col items-center gap-1 py-2 px-1 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <UserCircle className="h-4 w-4" />
              Mi Progreso
            </TabsTrigger>
          </TabsList>

          {/* FORMACIÓN */}
          <TabsContent value="formacion" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GraduationCap className="h-5 w-5 text-blue-400" />
                  Formación
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Aprende los fundamentos del entrenamiento y la salud.
                </p>
                
                <div className="grid gap-3">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Play className="h-8 w-8 text-blue-400" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">Videoteca</h4>
                        <p className="text-xs text-muted-foreground">Videos educativos sobre técnica y salud</p>
                      </div>
                      <Button variant="outline" size="sm" disabled>
                        <Lock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-blue-400" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">Guías PDF</h4>
                        <p className="text-xs text-muted-foreground">Documentos descargables</p>
                      </div>
                      <Button variant="outline" size="sm" disabled>
                        <Lock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* EQUIPAMIENTO / TIENDA GUARDIANSFIT */}
          <TabsContent value="equipamiento" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShoppingBag className="h-5 w-5 text-amber-400" />
                  Tienda GuardiansFit
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Equipamiento oficial GuardiansFit. Entrena con estilo.
                </p>
                
                {/* Productos de merchandising */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Camiseta Técnica */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_43333703-e2fe-4e22-b621-2a9a0890a0f7/artifacts/hulpxpa5_6F879F4F-7B18-4BEC-947F-99F17FEC45ED%20%281%29.jpeg"
                        alt="Camiseta GuardiansFit"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-foreground">Camiseta Técnica</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Tejido transpirable, número personalizado en la espalda
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-amber-400 font-bold text-lg">15€</span>
                        <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-400">
                          <MessageCircle className="h-4 w-4 mr-1" />
                          Pedir
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Polo */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_43333703-e2fe-4e22-b621-2a9a0890a0f7/artifacts/0cvgg75o_C207C8B5-0C43-4FAD-96A0-43587A319DD9.jpeg"
                        alt="Polo GuardiansFit"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-foreground">Polo Oficial</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Elegante y cómodo, número personalizado
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-amber-400 font-bold text-lg">25€</span>
                        <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-400">
                          <MessageCircle className="h-4 w-4 mr-1" />
                          Pedir
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Botella */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_43333703-e2fe-4e22-b621-2a9a0890a0f7/artifacts/f7jo83s6_88D8C4A8-CB1E-4D0F-ACE2-AFA32FFA6513.jpeg"
                        alt="Botella GuardiansFit"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-foreground">Botella 2.2L</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Mantén tu hidratación durante todo el entrenamiento
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-amber-400 font-bold text-lg">15€</span>
                        <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-400">
                          <MessageCircle className="h-4 w-4 mr-1" />
                          Pedir
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Toalla */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_43333703-e2fe-4e22-b621-2a9a0890a0f7/artifacts/9uijt78o_99512F24-1AB8-4306-9E58-79A3B3D2BAD5.jpeg"
                        alt="Toalla GuardiansFit"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-foreground">Toalla de Gym</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Microfibra de secado rápido
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-amber-400 font-bold text-lg">10€</span>
                        <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-400">
                          <MessageCircle className="h-4 w-4 mr-1" />
                          Pedir
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-center text-muted-foreground pt-4 border-t border-zinc-700">
                  Para hacer un pedido, contacta directamente con tu entrenador
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUPLEMENTACIÓN */}
          <TabsContent value="suplementacion" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Pill className="h-5 w-5 text-green-400" />
                  Suplementación
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Información basada en evidencia sobre suplementos.
                </p>
                
                <div className="grid gap-3">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-green-400" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">Guía de suplementos</h4>
                        <p className="text-xs text-muted-foreground">Qué funciona y qué no</p>
                      </div>
                      <Button variant="outline" size="sm" disabled>
                        <Lock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <ShoppingBag className="h-8 w-8 text-green-400" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">Descuentos</h4>
                        <p className="text-xs text-muted-foreground">Ofertas exclusivas para Guardians</p>
                      </div>
                      <Button variant="outline" size="sm" disabled>
                        <Lock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMUNIDAD */}
          <TabsContent value="comunidad" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5 text-purple-400" />
                  Comunidad
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Eventos y actividades de la comunidad GuardiansFit.
                </p>
                
                {/* Eventos */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-purple-400" />
                    Próximos Eventos
                  </h4>
                  
                  {loadingEventos ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                    </div>
                  ) : eventos.length === 0 ? (
                    <div className="p-6 bg-zinc-800/50 rounded-lg text-center">
                      <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No hay eventos programados por ahora.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        ¡Estate atento a las novedades!
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {eventos.map((evento) => (
                        <div 
                          key={evento.id}
                          className={`p-4 rounded-lg border ${evento.estoy_apuntado ? 'bg-purple-500/10 border-purple-500/40' : 'bg-zinc-800/50 border-zinc-700'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              {/* Tipo y título */}
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getTipoEventoColor(evento.tipo)}`}>
                                  {getTipoEventoLabel(evento.tipo)}
                                </span>
                                {evento.estoy_apuntado && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Apuntado
                                  </span>
                                )}
                              </div>
                              
                              <h5 className="font-semibold text-foreground">{evento.titulo}</h5>
                              <p className="text-sm text-muted-foreground mt-1">{evento.descripcion}</p>
                              
                              {/* Detalles */}
                              <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatFecha(evento.fecha)}
                                </span>
                                {evento.hora && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {evento.hora}h
                                  </span>
                                )}
                                {evento.lugar && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {evento.lugar}
                                  </span>
                                )}
                                {evento.max_participantes && (
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {evento.plazas_ocupadas}/{evento.max_participantes} plazas
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Botón apuntarse */}
                            <Button
                              size="sm"
                              variant={evento.estoy_apuntado ? "outline" : "default"}
                              className={evento.estoy_apuntado ? "border-red-500/50 text-red-400 hover:bg-red-500/10" : "bg-purple-600 hover:bg-purple-700"}
                              onClick={() => handleApuntarse(evento.id, evento.estoy_apuntado)}
                              disabled={apuntandose === evento.id}
                            >
                              {apuntandose === evento.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : evento.estoy_apuntado ? (
                                <>
                                  <UserMinus className="h-4 w-4 mr-1" />
                                  Desapuntarme
                                </>
                              ) : (
                                <>
                                  <UserPlus className="h-4 w-4 mr-1" />
                                  Apuntarme
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Grupo privado - Sigue bloqueado */}
                <div className="pt-4 border-t border-zinc-700">
                  <div className="p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg opacity-60">
                    <div className="flex items-center gap-3">
                      <MessageCircle className="h-6 w-6 text-muted-foreground" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-muted-foreground">Grupo privado</h4>
                        <p className="text-xs text-muted-foreground">Próximamente</p>
                      </div>
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* RECURSOS */}
          <TabsContent value="recursos" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FolderOpen className="h-5 w-5 text-orange-400" />
                  Recursos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Herramientas y documentos útiles.
                </p>
                
                <div className="grid gap-3">
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-orange-400" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">Plantillas</h4>
                        <p className="text-xs text-muted-foreground">Seguimiento y control</p>
                      </div>
                      <Button variant="outline" size="sm" disabled>
                        <Lock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-8 w-8 text-orange-400" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">Bibliografía</h4>
                        <p className="text-xs text-muted-foreground">Lecturas recomendadas</p>
                      </div>
                      <Button variant="outline" size="sm" disabled>
                        <Lock className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MI PROGRESO / ACCESO PERSONAL */}
          <TabsContent value="personal" className="space-y-4">
            {loadingProgress ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : progressReport?.has_plan ? (
              <>
                {/* Hero Card */}
                <Card className="bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border-primary/30 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-muted-foreground">¡Hola, Guardian!</p>
                        <h2 className="text-2xl font-bold text-foreground">{progressReport.client_name}</h2>
                      </div>
                      <div className="text-right">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 rounded-full">
                          <Trophy className="h-4 w-4 text-primary" />
                          <span className="text-sm font-semibold text-primary">{progressReport.level}</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-lg text-foreground/80 italic mb-4">"{progressReport.motivation}"</p>
                    
                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Mesociclo {progressReport.mesocycle}</span>
                        <span className="font-bold text-primary">{progressReport.percentage}%</span>
                      </div>
                      <div className="h-4 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-primary via-green-500 to-emerald-400 rounded-full transition-all duration-1000"
                          style={{ width: `${progressReport.percentage}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{progressReport.sessions_completed} sesiones completadas</span>
                        <span>{progressReport.sessions_remaining} restantes</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-card border-border">
                    <CardContent className="p-4 text-center">
                      <Calendar className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-foreground">{progressReport.sessions_completed}</p>
                      <p className="text-xs text-muted-foreground">Sesiones</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardContent className="p-4 text-center">
                      <Dumbbell className="h-6 w-6 text-amber-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-foreground">{progressReport.stats?.total_exercises || 0}</p>
                      <p className="text-xs text-muted-foreground">Ejercicios</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="h-6 w-6 text-green-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-foreground">{progressReport.stats?.total_sets || 0}</p>
                      <p className="text-xs text-muted-foreground">Series totales</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardContent className="p-4 text-center">
                      <Flame className="h-6 w-6 text-red-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-foreground">{progressReport.stats?.avg_rpe || '-'}</p>
                      <p className="text-xs text-muted-foreground">RPE Promedio</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Achievements */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Award className="h-5 w-5 text-yellow-400" />
                      Logros
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {progressReport.achievements?.map((achievement, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                            achievement.unlocked 
                              ? 'bg-yellow-500/20 border border-yellow-500/30' 
                              : 'bg-zinc-800/50 border border-zinc-700 opacity-50'
                          }`}
                        >
                          <span className="text-xl">{achievement.icon}</span>
                          <span className={`text-sm font-medium ${achievement.unlocked ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                            {achievement.name}
                          </span>
                          {achievement.unlocked && <Star className="h-4 w-4 text-yellow-400" />}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Consistency Score */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Target className="h-5 w-5 text-emerald-400" />
                      Tu Constancia
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-muted-foreground">Puntuación de constancia</span>
                          <span className="text-sm font-bold text-emerald-400">{progressReport.stats?.consistency_score || 0}/100</span>
                        </div>
                        <Progress value={progressReport.stats?.consistency_score || 0} className="h-2" />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {progressReport.stats?.consistency_score >= 80 
                        ? "¡Excelente constancia! Eres un Guardian ejemplar. 🏆"
                        : progressReport.stats?.consistency_score >= 50
                        ? "¡Buen ritmo! Sigue así y alcanzarás tus metas. 💪"
                        : "¡Cada sesión cuenta! Tu constancia mejorará con cada entrenamiento. 🚀"
                      }
                    </p>
                  </CardContent>
                </Card>

                {/* Info Card */}
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground text-center">
                      📊 Al finalizar el mesociclo recibirás un informe completo con tu progreso en fuerza, resistencia y más.
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Trophy className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center">
                    {progressReport?.message || "Aún no tienes un plan asignado"}
                  </p>
                  <p className="text-sm text-muted-foreground text-center mt-2">
                    Tu progreso aparecerá aquí cuando comiences tu entrenamiento
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </ClientLayout>
  );
};

export default ResourceCenterPage;
